import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import SearchFeaturePageShell from 'components/shell/SearchFeaturePageShell';
import IndexingPageGuard from 'features/operation-supervisor/IndexingPageGuard';
import FacetPolicyEditorShell from './FacetPolicyEditorShell';
import FacetPolicyList from './FacetPolicyList';
import { facetPolicyDraftRecoveryKey } from './facetPolicyDraftSlice';
import {
  FacetPolicyTranslationProvider,
  useFacetPolicyTranslation,
} from './facetPolicyI18n';
import styles from './CanonicalFacetPolicyPage.module.scss';
import 'features/facets/prototype/FacetsPrototype.scss';

const LEGACY_COMPATIBILITY_CODES = ['CAPABILITY_MISSING', 'UNKNOWN_ACTION'];

export function shouldUseLegacyFacetPage(error) {
  return !!error && LEGACY_COMPATIBILITY_CODES.includes(error.code);
}

export function effectRequiresReindex(effect) {
  return effect === 'reindex_required'
    || effect === 'after_reindex'
    || (effect && (effect.requires_reindex === true
      || effect.timing === 'after_reindex'
      || effect.state === 'pending_reindex'));
}

function CanonicalFacetPolicyPageContent({ api, indexingApi, fallback, deferredReindex }) {
  const t = useFacetPolicyTranslation();
  const [schema, setSchema] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [selectedFacet, setSelectedFacet] = useState(null);
  const [catalogRefreshKey, setCatalogRefreshKey] = useState(0);
  const [reindexStatus, setReindexStatus] = useState(null);
  const [languageScopes, setLanguageScopes] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [scopesReady, setScopesReady] = useState(false);

  useEffect(() => {
    let current = true;
    if (!api) { setStatus('fallback'); return undefined; }
    setSchema(null);
    setSelectedFacet(null);
    setLanguageScopes([]);
    setSelectedLanguage(null);
    setScopesReady(false);
    setStatus('loading');
    setError(null);
    const discovery = typeof api.listLanguages === 'function'
      && typeof api.forLanguage === 'function'
      ? Promise.resolve().then(() => api.listLanguages())
      : Promise.resolve({ languages: [], primary: null });
    discovery.then((result) => {
      if (!current) return;
      const languages = Array.isArray(result?.languages) ? result.languages.map(String) : [];
      const primary = result?.primary != null ? String(result.primary) : languages[0] || null;
      setLanguageScopes(languages);
      setSelectedLanguage(languages.includes(primary) ? primary : languages[0] || null);
      setScopesReady(true);
    }).catch((loadError) => {
      if (!current) return;
      setError(loadError && loadError.message
        ? loadError.message
        : t('facets.policy.error.schemaLoad', 'Facet schema failed to load.'));
      setStatus('failed');
    });
    return () => { current = false; };
  }, [api, t]);

  const scopedApi = useMemo(() => {
    if (!api || !scopesReady) return null;
    return selectedLanguage && typeof api.forLanguage === 'function'
      ? api.forLanguage(selectedLanguage)
      : api;
  }, [api, scopesReady, selectedLanguage]);

  useEffect(() => {
    let current = true;
    if (!scopesReady || !scopedApi) return undefined;
    setSchema(null);
    setSelectedFacet(null);
    setStatus('loading');
    setError(null);
    Promise.resolve().then(() => scopedApi.getSchema()).then((response) => {
      if (!current) return;
      const data = response && response.data ? response.data : {};
      if (!data.capabilities || data.capabilities.facet_policy_v1 !== true || !data.scope) {
        setStatus('fallback'); return;
      }
      setSchema(data); setStatus('ready');
    }).catch((loadError) => {
      if (!current) return;
      if (shouldUseLegacyFacetPage(loadError)) setStatus('fallback');
      else {
        setError(loadError && loadError.message
          ? loadError.message
          : t('facets.policy.error.schemaLoad', 'Facet schema failed to load.'));
        setStatus('failed');
      }
    });
    return () => { current = false; };
  }, [scopedApi, scopesReady, t]);

  const recoveryKey = useMemo(() => (
    schema ? facetPolicyDraftRecoveryKey(schema.scope) : null
  ), [schema]);
  const recovery = recoveryKey && typeof window !== 'undefined'
    ? window.localStorage.getItem(recoveryKey) : null;
  const requestReindex = async () => {
    if (!indexingApi || typeof indexingApi.startFull !== 'function') return;
    setReindexStatus('starting');
    try { await indexingApi.startFull(); setReindexStatus('started'); }
    catch (reindexError) { setReindexStatus(reindexError && reindexError.message ? reindexError.message : t('facets.policy.error.reindex', 'Reindex failed.')); }
  };

  if (status === 'fallback') return fallback;
  if (status === 'loading') {
    return <SearchFeaturePageShell pageId="customizationFilters" isLoading />;
  }
  if (status === 'failed') {
    return <SearchFeaturePageShell pageId="customizationFilters" error={error} />;
  }

  return (
    <div className="facets-prototype facets-production" data-testid="canonical-facet-policy-page">
      <IndexingPageGuard>
        <div>
          {languageScopes.length > 1 && (
            <div className={styles.scopeToolbar}>
              <label htmlFor="facet-policy-language">
                <span>{t('facets.policy.language.label', 'Facet language')}</span>
                <select
                  id="facet-policy-language"
                  aria-label={t('facets.policy.language.label', 'Facet language')}
                  value={selectedLanguage || ''}
                  onChange={(event) => {
                    setSelectedFacet(null);
                    setSelectedLanguage(event.target.value);
                  }}
                >
                  {languageScopes.map(value => (
                    <option key={value} value={value}>{value.toUpperCase()}</option>
                  ))}
                </select>
              </label>
              <p>{t(
                'facets.policy.language.hint',
                'Each language has isolated source values and may override the default facet policy.',
              )}
              </p>
            </div>
          )}
          <FacetPolicyList
            api={scopedApi}
            capabilities={schema.capabilities}
            onOpenFacet={setSelectedFacet}
            refreshKey={catalogRefreshKey}
          />
          {selectedFacet && (
            <FacetPolicyEditorShell
              api={scopedApi}
              facetKey={selectedFacet}
              shopId={schema.scope.shop}
              languageId={schema.scope.language}
              capabilities={schema.capabilities}
              recovery={recovery}
              onRecoveryChange={(value) => {
                if (!recoveryKey || typeof window === 'undefined') return;
                if (value) window.localStorage.setItem(recoveryKey, value);
                else window.localStorage.removeItem(recoveryKey);
              }}
              onClose={() => setSelectedFacet(null)}
              onSaved={({ effect } = {}) => {
                setCatalogRefreshKey(current => current + 1);
                if (deferredReindex && effectRequiresReindex(effect)
                  && indexingApi && typeof indexingApi.markRequired === 'function') {
                  indexingApi.markRequired().catch((markError) => {
                    setReindexStatus(markError && markError.message
                      ? markError.message
                      : t('facets.policy.error.reindex', 'Reindex failed.'));
                  });
                }
                setSelectedFacet(null);
              }}
              onRequestReindex={requestReindex}
            />
          )}
          {reindexStatus && <div role="status">{reindexStatus === 'started' ? t('facets.policy.reindexStarted', 'Full reindex started.') : reindexStatus}</div>}
        </div>
      </IndexingPageGuard>
    </div>
  );
}

CanonicalFacetPolicyPageContent.propTypes = {
  api: PropTypes.shape({
    getSchema: PropTypes.func.isRequired,
    listLanguages: PropTypes.func,
    forLanguage: PropTypes.func,
  }),
  indexingApi: PropTypes.shape({ startFull: PropTypes.func, markRequired: PropTypes.func }),
  fallback: PropTypes.node,
  deferredReindex: PropTypes.bool,
};
CanonicalFacetPolicyPageContent.defaultProps = {
  api: null,
  indexingApi: null,
  fallback: null,
  deferredReindex: false,
};

export default function CanonicalFacetPolicyPage({ translate, ...props }) {
  return (
    <FacetPolicyTranslationProvider translate={translate}>
      <CanonicalFacetPolicyPageContent {...props} />
    </FacetPolicyTranslationProvider>
  );
}

CanonicalFacetPolicyPage.propTypes = {
  ...CanonicalFacetPolicyPageContent.propTypes,
  translate: PropTypes.func,
};
CanonicalFacetPolicyPage.defaultProps = {
  ...CanonicalFacetPolicyPageContent.defaultProps,
  translate: null,
};
